// @ts-nocheck
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, Target, FileText, RefreshCw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface AdvertiserStats {
  activeCampaigns: number;
  totalAdRevenue: number;
  pendingProposals: number;
  renewalOpportunities: number;
}

const AdvertiserStatsOverview: React.FC = () => {
  const { profile } = useAuth();
  const [stats, setStats] = useState<AdvertiserStats>({
    activeCampaigns: 0,
    totalAdRevenue: 0,
    pendingProposals: 0,
    renewalOpportunities: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.id) {
      fetchStats();
    }
  }, [profile?.id]);

  const fetchStats = async () => {
    try {
      // Get active campaigns count
      const { data: campaigns, error: campaignsError } = await supabase
        .from('advertising_campaigns')
        .select('id, total_budget')
        .eq('campaign_status', 'live')
        .eq('advertiser_id', profile?.id);

      // Get pending proposals count
      const { data: proposals, error: proposalsError } = await supabase
        .from('advertising_proposals')
        .select('id')
        .eq('proposal_status', 'sent');

      // Get renewal opportunities (campaigns ending in next 30 days)
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      
      const { data: renewals, error: renewalsError } = await supabase
        .from('advertising_campaigns')
        .select('id')
        .eq('campaign_status', 'live')
        .lte('end_date', thirtyDaysFromNow.toISOString().split('T')[0]);

      if (!campaignsError && !proposalsError && !renewalsError) {
        const totalRevenue = campaigns?.reduce((sum, campaign) => 
          sum + (parseFloat(campaign.total_budget) || 0), 0) || 0;

        setStats({
          activeCampaigns: campaigns?.length || 0,
          totalAdRevenue: totalRevenue,
          pendingProposals: proposals?.length || 0,
          renewalOpportunities: renewals?.length || 0
        });
      }
    } catch (error) {
      console.error('Error fetching advertiser stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const statCards = [
    {
      title: "Active Campaigns",
      value: stats.activeCampaigns,
      icon: Target,
      color: "text-primary",
      bgColor: "bg-primary/10"
    },
    {
      title: "Total Ad Revenue",
      value: formatCurrency(stats.totalAdRevenue),
      icon: DollarSign,
      color: "text-secondary",
      bgColor: "bg-secondary/10"
    },
    {
      title: "Pending Proposals",
      value: stats.pendingProposals,
      icon: FileText,
      color: "text-accent",
      bgColor: "bg-accent/10"
    },
    {
      title: "Renewal Opportunities",
      value: stats.renewalOpportunities,
      icon: RefreshCw,
      color: "text-orange-600",
      bgColor: "bg-orange-100"
    }
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-20 bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {statCards.map((stat, index) => (
        <Card key={index} className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </p>
                <p className="text-2xl font-bold text-foreground">
                  {stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-full ${stat.bgColor}`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default AdvertiserStatsOverview;